import { useTranslation } from 'react-i18next';
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore, useAuthHydrated } from '@/stores/auth-store';
import { useSettingsStore, useSettingsHydrated, type PrepGoal } from '@/stores/settings-store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePrepTestStore, type PrepArea } from '@/stores/prep-test-store';
import { useYearWheelStore } from '@/stores/yearwheel-store';
import { useChecklistStore, useChecklistHydrated } from '@/stores/checklist-store';
import { useWaterStore, useWaterHydrated } from '@/stores/water-store';
import { useInventoryStore, useInventoryHydrated, type InventoryCategory, CATEGORY_LABELS } from '@/stores/inventory-store';
import useCustomChecklistStore from '@/stores/custom-checklist-store';
import { useNotesStore } from '@/stores/notes-store';
import { useShoppingListStore } from '@/stores/shopping-list-store';
import { useFavoriteStore, type Favorite } from '@/stores/favorite-store';
import { useMealPlanStore } from '@/stores/meal-plan-store';
import { Link, useNavigate } from 'react-router-dom';
import PrepTestModal from '@/components/PrepTestModal';
import RecommendationsModal from '@/components/RecommendationsModal';
import ModalBackdrop from '@/components/ModalBackdrop';
import LoadingScreen from '@/components/LoadingScreen';
import BannerDisplay from '@/components/BannerDisplay';
import NativePremiumNoticeModal from '@/components/NativePremiumNoticeModal';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import { useFamilyStore, useIsInFamily, useIsFamilyOwner } from '@/stores/family-store';
import { useAppModeStore } from '@/stores/app-mode-store';
import { defaultChecklists, type ChecklistItem } from '@/data/default-checklists';
import { ChevronRight, Clock, AlertTriangle, Lightbulb, Check, CalendarDays, ClipboardCheck, ClipboardList, Users, ShoppingCart, Target, Droplets, Utensils, Crown, Home, Heart, Phone, Radio, Zap, Shield, Car, Info, ExternalLink, Sprout, Circle, FileText, X, Plus, Search, Minus, MessageSquare, UtensilsCrossed } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { apiGet } from '@/services/api-client';
import { areaConfig as knowledgeAreaConfig } from '@/config/area-config';
import { getNativePlatform } from '@/utils/native-platform';

const COMMON_SHOPPING_ITEMS = [
  { name: 'Konserverade bönor', aisle: 'Konserver' },
  { name: 'Pasta', aisle: 'Torrvaror' },
  { name: 'Ris', aisle: 'Torrvaror' },
  { name: 'Havregröt', aisle: 'Torrvaror' },
  { name: 'Konserverad tomat', aisle: 'Konserver' },
  { name: 'Tonfisk', aisle: 'Konserver' },
  { name: 'Knäckebröd', aisle: 'Bröd & Bageri' },
  { name: 'Nötter', aisle: 'Godis & Snacks' },
  { name: 'Honung', aisle: 'Torrvaror' },
  { name: 'Mjöl', aisle: 'Torrvaror' },
  { name: 'Socker', aisle: 'Torrvaror' },
  { name: 'Salt', aisle: 'Torrvaror' },
  { name: 'Olivolja', aisle: 'Torrvaror' },
  { name: 'Kaffe', aisle: 'Drycker' },
  { name: 'Te', aisle: 'Drycker' },
  { name: 'Bröd', aisle: 'Bröd & Bageri' },
  { name: 'Mjölk', aisle: 'Mejeri' },
  { name: 'Smör', aisle: 'Mejeri' },
  { name: 'Ost', aisle: 'Mejeri' },
  { name: 'Ägg', aisle: 'Mejeri' },
  { name: 'Kött', aisle: 'Kött & Chark' },
  { name: 'Fisk', aisle: 'Fisk & Skaldjur' },
  { name: 'Grönsaker', aisle: 'Frukt & Grönt' },
  { name: 'Frukt', aisle: 'Frukt & Grönt' },
  { name: 'Potatis', aisle: 'Frukt & Grönt' },
  { name: 'Lök', aisle: 'Frukt & Grönt' },
  { name: 'Vitlök', aisle: 'Frukt & Grönt' },
  { name: 'Tomat', aisle: 'Frukt & Grönt' },
  { name: 'Gurka', aisle: 'Frukt & Grönt' },
  { name: 'Paprika', aisle: 'Frukt & Grönt' },
];

const COMMON_AISLES = [
  'Frukt & Grönt',
  'Mejeri',
  'Kött & Chark',
  'Fisk & Skaldjur',
  'Bröd & Bageri',
  'Konserver',
  'Torrvaror',
  'Fryst',
  'Drycker',
  'Godis & Snacks',
  'Kemi & Städning',
  'Hygien',
  'Djurmat',
  'Hushåll',
  'Baby',
];

// Area configuration for Beredskapsååtgärder card (matching KnowledgePage with hex colors)
type PrepAreaKey = 'water' | 'food' | 'shelter' | 'health' | 'communication' | 'information' | 'energy' | 'safety' | 'transport' | 'shopping' | 'gardening' | 'other';

const areaConfig: Record<PrepAreaKey, { icon: LucideIcon; color: string }> = {
  water: { icon: Droplets, color: '#6bc5ff' },
  food: { icon: Utensils, color: '#ffcc6b' },
  shelter: { icon: Home, color: '#b86bff' },
  health: { icon: Heart, color: '#ff6b6b' },
  communication: { icon: Radio, color: '#6bffea' },
  information: { icon: Info, color: '#ff6bb3' },
  energy: { icon: Zap, color: '#ffa06b' },
  safety: { icon: Shield, color: '#6bffb3' },
  transport: { icon: Car, color: '#6b8aff' },
  shopping: { icon: ShoppingCart, color: '#6bffd4' },
  gardening: { icon: Sprout, color: '#d4ff6b' },
  other: { icon: Circle, color: '#9e9e9e' },
};

const recommendationAreaConfig: Record<PrepArea, { icon: LucideIcon; color: string }> = {
  water: { icon: knowledgeAreaConfig.water.icon, color: knowledgeAreaConfig.water.color },
  food: { icon: knowledgeAreaConfig.food.icon, color: knowledgeAreaConfig.food.color },
  energy: { icon: knowledgeAreaConfig.energy.icon, color: knowledgeAreaConfig.energy.color },
  health: { icon: knowledgeAreaConfig.health.icon, color: knowledgeAreaConfig.health.color },
  information: { icon: knowledgeAreaConfig.information.icon, color: knowledgeAreaConfig.information.color },
  shelter: { icon: knowledgeAreaConfig.shelter.icon, color: knowledgeAreaConfig.shelter.color },
  transport: { icon: knowledgeAreaConfig.transport.icon, color: knowledgeAreaConfig.transport.color },
};

const recommendationFallbackArea: { icon: LucideIcon; color: string } = {
  icon: knowledgeAreaConfig.safety.icon,
  color: knowledgeAreaConfig.safety.color,
};

// Separate config for Underhåll/YearWheel card (with Tailwind classes)
const yearWheelAreaConfig: Record<PrepAreaKey, { icon: LucideIcon; color: string; iconColor: string; name: string }> = {
  water: { icon: Droplets, color: 'bg-blue-900/30 text-blue-300 border-blue-700', iconColor: 'text-blue-400', name: 'Vatten' },
  food: { icon: Utensils, color: 'bg-amber-900/30 text-amber-300 border-amber-700', iconColor: 'text-amber-400', name: 'Mat' },
  shelter: { icon: Home, color: 'bg-stone-700/30 text-stone-300 border-stone-600', iconColor: 'text-stone-400', name: 'Skydd' },
  health: { icon: Heart, color: 'bg-red-900/30 text-red-300 border-red-700', iconColor: 'text-red-400', name: 'Hälsa' },
  communication: { icon: Phone, color: 'bg-indigo-900/30 text-indigo-300 border-indigo-700', iconColor: 'text-indigo-400', name: 'Kommunikation' },
  information: { icon: Radio, color: 'bg-purple-900/30 text-purple-300 border-purple-700', iconColor: 'text-purple-400', name: 'Information' },
  energy: { icon: Zap, color: 'bg-yellow-900/30 text-yellow-300 border-yellow-700', iconColor: 'text-yellow-400', name: 'Energi' },
  safety: { icon: Shield, color: 'bg-slate-700/30 text-slate-300 border-slate-600', iconColor: 'text-slate-400', name: 'Säkerhet' },
  transport: { icon: Car, color: 'bg-green-900/30 text-green-300 border-green-700', iconColor: 'text-green-400', name: 'Transport' },
  shopping: { icon: ShoppingCart, color: 'bg-teal-900/30 text-teal-300 border-teal-700', iconColor: 'text-teal-400', name: 'Inköp' },
  gardening: { icon: Sprout, color: 'bg-lime-900/30 text-lime-300 border-lime-700', iconColor: 'text-lime-400', name: 'Odla' },
  other: { icon: Circle, color: 'bg-gray-700/30 text-gray-300 border-gray-600', iconColor: 'text-gray-400', name: 'Övrigt' },
};

// Use the shared `areaConfig` for icon and color consistency

const PREP_GOAL_DAYS: Record<PrepGoal, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

const MONTH_DAY_PATTERN = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

function parseMonthDay(value: string | null | undefined): number | null {
  if (!value || !MONTH_DAY_PATTERN.test(value)) {
    return null;
  }

  const [monthText, dayText] = value.split('-');
  const month = Number(monthText);
  const day = Number(dayText);
  const testDate = new Date(2001, month - 1, day);

  if (testDate.getMonth() !== month - 1 || testDate.getDate() !== day) {
    return null;
  }

  return month * 100 + day;
}

function getCurrentMonthDayValue(date = new Date()): number {
  return (date.getMonth() + 1) * 100 + date.getDate();
}

function isWithinRecurringMonthDayRange(
  currentValue: number,
  startValue: number | null,
  endValue: number | null,
): boolean {
  if (startValue === null && endValue === null) {
    return true;
  }

  if (startValue !== null && endValue !== null) {
    if (startValue <= endValue) {
      return currentValue >= startValue && currentValue <= endValue;
    }

    return currentValue >= startValue || currentValue <= endValue;
  }

  if (startValue !== null) {
    return currentValue >= startValue;
  }

  if (endValue !== null) {
    return currentValue <= endValue;
  }

  return true;
}

export default function DashboardPage() {
  const nativePlatform = getNativePlatform();
  const [showNativePremiumNotice, setShowNativePremiumNotice] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, fetchUser } = useAuthStore();
  const { data: supportSummary } = useQuery<{ unreadCount: number }>({
    queryKey: ['dashboard-support-summary', user?.id],
    queryFn: async () => {
      if (!user) return { unreadCount: 0 };
      try {
        const res = await apiGet<{ summary?: { unreadCount?: number } }>('/support/cases');
        return {
          unreadCount: Number(res.success && res.data?.summary?.unreadCount ? res.data.summary.unreadCount : 0),
        };
      } catch {
        return { unreadCount: 0 };
      }
    },
    refetchInterval: 60 * 1000,
    enabled: !!user,
  });
  const { setMode: setAppMode } = useAppModeStore();
  const authHydrated = useAuthHydrated();
  const shoppingListRef = useRef<HTMLDivElement>(null);
  const checklistHydrated = useChecklistHydrated();
  const waterHydrated = useWaterHydrated();
  const inventoryHydrated = useInventoryHydrated();
  const { notes, fetchFromServer: fetchNotes } = useNotesStore();
  const shoppingLists = useShoppingListStore(state => state.lists);
  const shoppingListItems = useShoppingListStore(state => state.items);
  const createItem = useShoppingListStore(state => state.createItem);
  const updateItem = useShoppingListStore(state => state.updateItem);
  const fetchShoppingLists = useShoppingListStore(state => state.fetchFromServer);
  const mealPlanItems = useMealPlanStore(state => state.items);
  const fetchMealPlan = useMealPlanStore(state => state.fetchFromServer);
  const { prepGoal, familyMembers, userDailyCalories, getTimeLevelsForGoal, fetchFromServer: fetchSettings, vmaRegion } = useSettingsStore();
  // Also subscribe to raw settings values so we can explicitly prefer owner-scoped
  // settings when viewing as a family member. This avoids any transient flicker
  // where the local user's persisted settings would otherwise be shown.
  const settingsFamilyMembers = useSettingsStore((s) => s.familyMembers);
  const settingsUserDailyCalories = useSettingsStore((s) => s.userDailyCalories);
  const { hidePrepTestCard, setHidePrepTestCard } = useSettingsStore();
  const settingsHydrated = useSettingsHydrated();
  const { 
    recommendations, 
    markRecommendationComplete,
    hasCompletedTest,
    fetchFromServer: fetchPrepTest,
  } = usePrepTestStore();
  const { getOverdueTasks, getTasksByMonth } = useYearWheelStore();
  const fetchYearWheel = useYearWheelStore((s) => s.fetchFromServer);
  const fetchFamily = useFamilyStore((s) => s.fetchFamily);
  const isInFamily = useIsInFamily();
  const isFamilyOwner = useIsFamilyOwner();

  // Track desktop sidebar state
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);

  // Ensure app mode is set to 'home' when Dashboard loads
  useEffect(() => {
    setAppMode('home');
  }, [setAppMode]);

  useEffect(() => {
    const handleStorageChange = () => {
      const collapsed = sessionStorage.getItem('desktopSidebarCollapsed') === 'true';
      setDesktopSidebarCollapsed(collapsed);
    };

    // Initial check
    handleStorageChange();

    // Listen for custom event from Layout
    window.addEventListener('sidebar-toggle', handleStorageChange);
    
    return () => {
      window.removeEventListener('sidebar-toggle', handleStorageChange);
    };
  }, []);

  // Compute which settings should be used for the household card. If the
  // current user is an invited family member (not the owner), prefer the
  // settings store values that were fetched for the owner's profile.
  const displayFamilyMembers = (isInFamily && !isFamilyOwner) ? settingsFamilyMembers : familyMembers;
  const displayUserDailyCalories = (isInFamily && !isFamilyOwner) ? settingsUserDailyCalories : userDailyCalories;

  const adultsCount = 1 + displayFamilyMembers.filter(member => member.role === 'adult').length;
  const childrenCount = displayFamilyMembers.filter(member => member.role === 'child').length;
  const petCount = displayFamilyMembers.filter(member => member.role === 'pet').length;
  
  // Hämta data från stores
  const { itemStatus, fetchFromServer: fetchChecklists } = useChecklistStore();
  const { stats: waterStats, fetchFromServer: fetchWater } = useWaterStore();
  const { fetchFromServer: fetchInventory } = useInventoryStore();
  const inventoryItems = useInventoryStore((state) => state.items);
  
  // Custom checklists
  const customLists = useCustomChecklistStore(state => state.lists);
  const customItems = useCustomChecklistStore(state => state.items);
  const toggleCustomItem = useCustomChecklistStore(state => state.toggleItem);
  const fetchCustomChecklists = useCustomChecklistStore(state => state.fetchFromServer);
  const checkRecurring = useCustomChecklistStore(state => state.checkRecurring);
  const dashboardLists = customLists.filter(list => list.showOnDashboard).sort((a, b) => {
    const oa = typeof a.order === 'number' ? a.order : 0;
    const ob = typeof b.order === 'number' ? b.order : 0;
    if (oa !== ob) return oa - ob;
    return a.createdAt.localeCompare(b.createdAt);
  });
  
  const hasTest = hasCompletedTest();
  const [showPrepTest, setShowPrepTest] = useState(false);
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false);
  const [prepTestLoaded, setPrepTestLoaded] = useState(false);
  const [completingRecommendationIds, setCompletingRecommendationIds] = useState<Set<string>>(new Set());
  const [viewingNote, setViewingNote] = useState<{ id: string; title: string; content: string } | null>(null);
  
  // Only show loading screen on first load after login (not on navigation back)
  const [isInitialLoading, setIsInitialLoading] = useState(() => {
    // Check if we've already loaded data in this session
    const hasLoadedBefore = sessionStorage.getItem('dashboardDataLoaded') === 'true';
    return !hasLoadedBefore;
  });
  
  // Quick-add item modal
  const [showQuickAddItem, setShowQuickAddItem] = useState(false);
  const [quickAddListId, setQuickAddListId] = useState<string | null>(null);
  const [quickAddItemForm, setQuickAddItemForm] = useState<{
    name: string;
    category: InventoryCategory | '';
    notes: string;
    link: string;
    quantity: number;
    price: string;
    store: string;
    aisle: string;
  }>({
    name: '',
    category: 'mat',
    notes: '',
    link: '',
    quantity: 1,
    price: '0',
    store: '',
    aisle: '',
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionSearch, setSuggestionSearch] = useState('');
  const [showStoreSuggestions, setShowStoreSuggestions] = useState(false);
  const [storeSuggestionSearch, setStoreSuggestionSearch] = useState('');
  const [showAisleSuggestions, setShowAisleSuggestions] = useState(false);
  const [aisleSuggestionSearch, setAisleSuggestionSearch] = useState('');

  // Quick add favorite dropdown state
  const [showQuickAddFavorite, setShowQuickAddFavorite] = useState<string | null>(null);
  const [quickAddFavoriteSearch, setQuickAddFavoriteSearch] = useState('');
  const [addedFavoriteId, setAddedFavoriteId] = useState<string | null>(null);

  // Filtered suggestions for quick-add modal
  const filteredSuggestions = useMemo(() => {
    if (!suggestionSearch) return COMMON_SHOPPING_ITEMS;
    return COMMON_SHOPPING_ITEMS.filter(s => 
      s.name.toLowerCase().includes(suggestionSearch.toLowerCase())
    );
  }, [suggestionSearch]);

  // Favorite store
  const favorites = useFavoriteStore(state => state.favorites);
  const fetchFavorites = useFavoriteStore(state => state.fetchFromServer);

  // Filtered favorites for quick add
  const filteredFavorites = useMemo(() => {
    if (!quickAddFavoriteSearch.trim()) return favorites;
    const search = quickAddFavoriteSearch.toLowerCase();
    return favorites.filter(fav => 
      fav.name.toLowerCase().includes(search) ||
      (fav.notes && fav.notes.toLowerCase().includes(search)) ||
      (fav.store && fav.store.toLowerCase().includes(search)) ||
      (fav.aisle && fav.aisle.toLowerCase().includes(search))
    );
  }, [favorites, quickAddFavoriteSearch]);

  // Handle recommendation completion with 1-second delay and visual feedback
  const handleToggleRecommendation = (id: string) => {
    // Om redan markerad som komplett, toggle direkt
    const rec = recommendations.find(r => r.id === id);
    if (rec?.completed) {
      markRecommendationComplete(id, false);
      return;
    }

    // Annars visa visuell feedback i 1 sekund innan markering
    setCompletingRecommendationIds(prev => new Set(prev).add(id));
    setTimeout(() => {
      markRecommendationComplete(id, true);
      setCompletingRecommendationIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 1000);
  };

  const existingStores = useMemo(() => {
    const stores = new Set<string>();
    shoppingListItems.forEach(item => {
      if (item.store) stores.add(item.store);
    });
    return Array.from(stores).sort();
  }, [shoppingListItems]);

  const filteredStores = useMemo(() => {
    if (!storeSuggestionSearch) return existingStores;
    return existingStores.filter(s => 
      s.toLowerCase().includes(storeSuggestionSearch.toLowerCase())
    );
  }, [existingStores, storeSuggestionSearch]);

  const filteredAisles = useMemo(() => {
    if (!aisleSuggestionSearch) return COMMON_AISLES;
    return COMMON_AISLES.filter(a => 
      a.toLowerCase().includes(aisleSuggestionSearch.toLowerCase())
    );
  }, [aisleSuggestionSearch]);
  
  // Scroll lock for note modal
  useLayoutEffect(() => {
    if (viewingNote) {
      const scrollY = window.scrollY;
      document.documentElement.classList.add('modal-open');
      document.body.style.top = `-${scrollY}px`;
      
      return () => {
        document.documentElement.classList.remove('modal-open');
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [viewingNote]);
  
  // Scroll lock for quick-add item modal
  useLayoutEffect(() => {
    if (showQuickAddItem) {
      try { lockScroll(); } catch { /* noop */ }
      return () => {
        try { unlockScroll(); } catch { /* noop */ }
      };
    }
  }, [showQuickAddItem]);

  // Scroll lock for suggestions modals
  useLayoutEffect(() => {
    if (showSuggestions || showStoreSuggestions || showAisleSuggestions) {
      try { lockScroll(); } catch { /* noop */ }
      return () => {
        try { unlockScroll(); } catch { /* noop */ }
      };
    }
  }, [showSuggestions, showStoreSuggestions, showAisleSuggestions]);
  
  // Restore modal state if returning from knowledge page
  useLayoutEffect(() => {
    try {
      const shouldShowModal = sessionStorage.getItem('recommendationsModalOpen') === 'true';
      
      if (shouldShowModal) {
        setShowRecommendationsModal(true);
        sessionStorage.removeItem('recommendationsModalOpen');
      }
    } catch {
      // ignore sessionStorage errors
    }
  }, []);
  
  // Check IP blocking on mount
  useEffect(() => {
    if (authHydrated && isAuthenticated) {
      // Perform IP check immediately when opening Dashboard
      apiGet('/check-ip').catch(() => {
        // If IP is blocked, apiClient will automatically handle logout
        // No need to do anything here
      });
    }
  }, [authHydrated, isAuthenticated]);
  
  // Hämta data vid mount om inloggad (optimerad - vänta endast på kritiska hydrations)
  useEffect(() => {
    // Only wait for critical hydrations needed for main dashboard stats
    // (beredskapsnivå, vattenberedskap, kaloriberedskap)
    if (authHydrated && settingsHydrated && checklistHydrated && waterHydrated && inventoryHydrated && isAuthenticated) {
      (async () => {
        // Phase 1: Load critical dependencies first (family & settings)
        // These must complete before other fetches that depend on family context
        try {
          const phase1Promises = [];
          
          if (user?.familyId) {
            phase1Promises.push(fetchFamily(user.familyId).catch(() => {}));
          }
          phase1Promises.push(fetchSettings().catch(() => {}));
          
          await Promise.all(phase1Promises);
        } catch { /* noop */ }

        // Phase 2: Fetch critical data needed for main stats (parallel)
        const familyId = useFamilyStore.getState().family?.id;
        
        const criticalPromises = [
          fetchChecklists().catch(() => {}),
          fetchWater(familyId).catch(() => {}),
          fetchInventory().catch(() => {}),
          fetchCustomChecklists().catch(() => {}),
          checkRecurring().catch(() => {}),
          fetchPrepTest(familyId).then(() => setPrepTestLoaded(true)).catch(() => setPrepTestLoaded(true)),
        ];

        await Promise.all(criticalPromises);

        // Done loading critical data - show the page
        sessionStorage.setItem('dashboardDataLoaded', 'true');
        setIsInitialLoading(false);

        // Phase 3: Load non-critical data in background (don't block page render)
        // These can load after the page is visible
        const backgroundPromises = [
          fetchNotes().catch(() => {}),
          fetchYearWheel().catch(() => {}),
          fetchShoppingLists().catch(() => {}),
          fetchMealPlan(familyId).catch(() => {}),
          fetchFavorites(familyId).catch(() => {}),
          fetchUser().catch(() => {}),
        ];

        // Pre-fetch family chat messages if user has a family (warms up cache for faster chat open)
        if (user?.familyId) {
          backgroundPromises.push(
            queryClient.prefetchQuery({
              queryKey: ['family-chat-messages-preview'],
              queryFn: async () => {
                const res = await apiGet<{ 
                  messages: { id: string; fromName?: string; fromId?: string; text: string; createdAt: string; imageUrl?: string }[];
                  meta: { hasMore?: boolean };
                }>('/family/messages?limit=3');
                return res.success && res.data ? res.data : { messages: [], meta: {} };
              },
              staleTime: 30 * 1000, // Consider fresh for 30 seconds
            }).catch(() => {})
          );
        }

        Promise.all(backgroundPromises).catch(() => {});
      })();
    }
  }, [
    authHydrated,
    settingsHydrated,
    checklistHydrated,
    waterHydrated,
    inventoryHydrated,
    isAuthenticated,
    user?.familyId,
    fetchFamily,
    fetchSettings,
    fetchChecklists,
    fetchNotes,
    fetchWater,
    fetchInventory,
    fetchUser,
    fetchYearWheel,
    fetchCustomChecklists,
    checkRecurring,
    fetchPrepTest,
    fetchMealPlan,
    fetchFavorites,
    fetchShoppingLists,
    queryClient,
  ]);

  // Stäng quick add favorite dropdown när man klickar utanför
  useEffect(() => {
    if (!showQuickAddFavorite) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.quick-add-fav-dropdown') && !target.closest('button[title="Lägg till favorit snabbt"]')) {
        setShowQuickAddFavorite(null);
        setQuickAddFavoriteSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQuickAddFavorite]);
  
  // Hämta uppgifter för aktuell månad från Årshjulet (inkl. avklarade)
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const monthName = new Intl.DateTimeFormat('sv-SE', { month: 'long' }).format(now);
  const monthNameCapitalized = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  
  // Filter tasks to only show completed ones from last 1 day, or upcoming ones
  const oneDayAgo = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
  const currentMonthTasks = getTasksByMonth(currentMonth, currentYear)
    .filter(task => {
      if (!task.lastCompleted) return true; // Show uncompleted tasks
      const lastCompletedDate = new Date(task.lastCompleted);
      return lastCompletedDate >= oneDayAgo; // Only show completed if within last 1 day
    })
    .slice(0, 6);
  
  // Hämta försenade uppgifter separat
  // upcomingTasks is not used on this card; we keep separate currentMonthTasks and overdueTasks
  const overdueTasks = getOverdueTasks();

  // Banner carousel state
  interface BannerData {
    id: string;
    title: string;
    description: string;
    image: string;
    link: string;
    openInNewTab: boolean;
    showInFamily: 'on' | 'off' | 'both';
    showInPremium: 'on' | 'off' | 'both';
    daysSinceAccountCreatedMin?: number;
    daysSinceAccountCreatedMax?: number;
    startDate?: string | null;
    endDate?: string | null;
    backgroundPositionY?: number; // -20 to 20, default 0 (center)
    firstSignIn?: boolean; // Show this banner first on user's first sign in
  }

  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [contentBanners, setContentBanners] = useState<BannerData[]>([]);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  const [hasSeenRecommendations, setHasSeenRecommendations] = useState(() => 
    // Check both localStorage (for immediate feedback) and user data (from DB)
    localStorage.getItem('hasSeenRecommendationsModal') === 'true' || user?.hasSeenRecommendationsModal === true
  );
  
  // Track screen size for background positioning
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Update hasSeenRecommendations when user data changes
  useEffect(() => {
    if (user?.hasSeenRecommendationsModal === true) {
      setHasSeenRecommendations(true);
    }
  }, [user?.hasSeenRecommendationsModal]);
  
  // Load banners from JSON file
  useEffect(() => {
    const isInFamily = !!user?.familyId;
    const isPremium = user?.subscriptionTier === 'premium';
    const currentMonthDayValue = getCurrentMonthDayValue();
    
    // Calculate days since account created
    let daysSinceCreated: number | null = null;
    if (user?.createdAt) {
      const createdDate = new Date(user.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - createdDate.getTime();
      daysSinceCreated = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }
    
    fetch('/banners/banners.json')
      .then(res => res.json())
      .then((data: BannerData[]) => {
        // Filter banners based on family and premium status
        const filtered = data.filter(banner => {
          // Check family filter
          const familyMatch = 
            banner.showInFamily === 'both' ||
            (banner.showInFamily === 'on' && isInFamily) ||
            (banner.showInFamily === 'off' && !isInFamily);
          
          // Check premium filter
          const premiumMatch = 
            banner.showInPremium === 'both' ||
            (banner.showInPremium === 'on' && isPremium) ||
            (banner.showInPremium === 'off' && !isPremium);
          
          // Check days since account created filters
          let daysMatch = true;
          if (daysSinceCreated !== null) {
            if (banner.daysSinceAccountCreatedMin != null && daysSinceCreated < banner.daysSinceAccountCreatedMin) {
              daysMatch = false;
            }
            if (banner.daysSinceAccountCreatedMax != null && daysSinceCreated > banner.daysSinceAccountCreatedMax) {
              daysMatch = false;
            }
          }

          const seasonalMatch = isWithinRecurringMonthDayRange(
            currentMonthDayValue,
            parseMonthDay(banner.startDate),
            parseMonthDay(banner.endDate),
          );
          
          return familyMatch && premiumMatch && daysMatch && seasonalMatch;
        });
        
        // Separate firstSignIn banners from regular banners
        const firstSignInBanners = filtered.filter(b => b.firstSignIn === true);
        const regularBanners = filtered.filter(b => b.firstSignIn !== true);
        
        // Shuffle regular banners using Fisher-Yates
        const shuffledRegular = [...regularBanners];
        for (let i = shuffledRegular.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffledRegular[i], shuffledRegular[j]] = [shuffledRegular[j], shuffledRegular[i]];
        }
        
        // Show firstSignIn banners ONLY if user has NOT seen recommendations modal yet
        // Once they open the modal, they're no longer "first time"
        const finalBanners = !hasSeenRecommendations 
          ? [...firstSignInBanners, ...shuffledRegular]
          : shuffledRegular;
        
        setContentBanners(finalBanners);
      })
      .catch(err => console.error('Failed to load banners:', err));
  }, [user?.familyId, user?.subscriptionTier, user?.createdAt, hasSeenRecommendations]);
  
  // Total banners: logo first, then content banners
  const totalBanners = 1 + contentBanners.length;

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      // Swipe left - go to next
      setCurrentBannerIndex((prev) => (prev + 1) % totalBanners);
    } else if (isRightSwipe) {
      // Swipe right - go to previous
      setCurrentBannerIndex((prev) => (prev - 1 + totalBanners) % totalBanners);
    }
  };

  const handleCarouselClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    
    // On desktop: very small left edge goes back, right 5% advances to next
    // On mobile: no left click zone, right click zone advances
    if (isDesktop) {
      // Desktop: Click on left 2% - go to previous
      if (x < width * 0.02) {
        setCurrentBannerIndex((prev) => (prev - 1 + totalBanners) % totalBanners);
        return;
      }
      // Desktop: Click on right 5% - go to next
      if (x > width * 0.95) {
        setCurrentBannerIndex((prev) => (prev + 1) % totalBanners);
        return;
      }
    } else {
      // Mobile: Click on right 25% - go to next (no left zone)
      if (x > width * 0.75) {
        setCurrentBannerIndex((prev) => (prev + 1) % totalBanners);
        return;
      }
    }
    
    // Click in center - navigate if it's a content banner
    if (currentBannerIndex > 0) {
      const banner = contentBanners[currentBannerIndex - 1];
      if (banner?.link) {
        // Special handling for modal links
        if (banner.link === '#recommendations-modal') {
          setShowRecommendationsModal(true);
        } else if (banner.openInNewTab) {
          window.open(banner.link, '_blank', 'noopener,noreferrer');
        } else {
          navigate(banner.link);
        }
      }
    }
  };

  // Auto-rotate banners - paused when page is not visible
  useEffect(() => {
    if (totalBanners === 0) return;
    // Don't auto-rotate during initial loading
    if (isInitialLoading) return;
    
    // First banner shows for 3 seconds, others for 25 seconds
    const duration = currentBannerIndex === 0 ? 3000 : 25000;
    
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let startTime = Date.now();
    let remainingTime = duration;
    
    const scheduleNext = () => {
      timeout = setTimeout(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % totalBanners);
      }, remainingTime);
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - pause the timer
        if (timeout) {
          clearTimeout(timeout);
          timeout = undefined;
        }
        // Calculate remaining time
        const elapsed = Date.now() - startTime;
        remainingTime = Math.max(0, remainingTime - elapsed);
      } else {
        // Page is visible again - resume timer with remaining time
        startTime = Date.now();
        scheduleNext();
      }
    };
    
    // Start timer if page is visible
    if (!document.hidden) {
      scheduleNext();
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [totalBanners, currentBannerIndex, isInitialLoading]);

  // Beräkna statistik från riktiga stores
  // Begränsa checklist-statistik till de items som ingår i användarens valda beredskapsmål
  // `defaultChecklists` is static content imported from a data file — it does not
  // change at runtime. Excluding it from deps avoids unnecessary recomputations.
  const relevantChecklistItems = useMemo(() => {
    const levels = getTimeLevelsForGoal();
    return (defaultChecklists as ChecklistItem[]).filter(item => levels.includes(item.level));
  }, [getTimeLevelsForGoal]);

  const completedChecklistItems = relevantChecklistItems.filter(item => itemStatus[item.id]?.isCompleted).length;
  const totalChecklistItems = relevantChecklistItems.length || 0;

  const totalKcal = useMemo(() => (
    inventoryItems
      .filter(item => item.category === 'mat' && item.kcalPerUnit)
      .reduce((sum, item) => sum + (item.kcalPerUnit! * item.quantity), 0)
  ), [inventoryItems]);

  // Beräkna totalt dagligt kaloribehov från faktiska inställningar
  const { totalDailyKcal, adultsKcal, childrenKcal } = useMemo(() => {
    // Kontohavaren (alltid vuxen) — prefer displayUserDailyCalories which may
    // represent the owner's value when in family mode.
    let adultsTotal = displayUserDailyCalories || 2000;
    let childrenTotal = 0;

    // Familjemedlemmar (exkl husdjur) based on the chosen displayFamilyMembers
    displayFamilyMembers.forEach(member => {
      if (member.role === 'adult') {
        adultsTotal += member.dailyCalories || 2000;
      } else if (member.role === 'child') {
        childrenTotal += member.dailyCalories || 1500;
      }
    });

    return {
      totalDailyKcal: adultsTotal + childrenTotal,
      adultsKcal: adultsTotal,
      childrenKcal: childrenTotal,
    };
  }, [displayUserDailyCalories, displayFamilyMembers]);

  const daysOfFood = totalDailyKcal > 0 ? Math.floor(totalKcal / totalDailyKcal) : 0;
  const caloriesGoal = totalDailyKcal * (PREP_GOAL_DAYS[prepGoal] ?? 7);

  // Stats for the Prep Level card — only derived from checklist progress
  const stats = useMemo(() => ({
    completedItems: completedChecklistItems,
    totalItems: totalChecklistItems,
  }), [completedChecklistItems, totalChecklistItems]);

  // Beräkna procentsatser för vatten, kalorier och checklistor (enligt valt beredskapsmål)
  const { progress: waterPercent = 0 } = (waterStats && typeof (waterStats as { progress?: unknown }).progress === 'number') ? (waterStats as { progress: number }) : { progress: 0 };
  const expiredWaterPenalty = Array.isArray(waterStats?.containersNeedingRefresh)
    ? waterStats.containersNeedingRefresh.length
    : 0;

  const caloriesPercent = useMemo(() => {
    if (!caloriesGoal || caloriesGoal <= 0) return 0;
    const pct = Math.round((totalKcal / caloriesGoal) * 100);
    return Math.max(0, Math.min(100, pct));
  }, [totalKcal, caloriesGoal]);

  const checklistPercent = useMemo(() => {
    const levels = getTimeLevelsForGoal();
    const relevant = (defaultChecklists as ChecklistItem[]).filter((item) => levels.includes(item.level));
    if (relevant.length === 0) return 0;
    const completed = relevant.filter((item) => itemStatus[item.id]?.isCompleted).length;
    return Math.round((completed / relevant.length) * 100);
  }, [getTimeLevelsForGoal, itemStatus]);

  // Prep level percent is weighted: 25% water (max), 50% calories, 25% checklists
  const prepLevelPercent = useMemo(() => {
    // Cap water at 100% for calculation (so max contribution is 25%)
    const cappedWaterPercent = Math.min(Number(waterPercent), 100);
    
    // Calculate weighted contribution: water 25%, calories 50%, checklists 25%
    const waterContribution = cappedWaterPercent * 0.25;
    const caloriesContribution = Number(caloriesPercent) * 0.50;
    const checklistContribution = Number(checklistPercent) * 0.25;

    const weightedPercent = Math.round(waterContribution + caloriesContribution + checklistContribution);
    return Math.max(0, weightedPercent - expiredWaterPenalty);
  }, [waterPercent, caloriesPercent, checklistPercent, expiredWaterPenalty]);

  // Beredskapsområden med framsteg baserat på VALD beredskapsnivå (t.ex. 7d)
  const prepAreas = useMemo(() => {
    const levels = getTimeLevelsForGoal();
    const allItems = defaultChecklists as ChecklistItem[];

    const areaKeys: PrepAreaKey[] = [
      'water', 'food', 'shelter', 'health', 'communication', 'information', 'energy', 'safety', 'transport'
    ];

    return areaKeys.map((key) => {
      const itemsForLevel = allItems.filter(i => i.area === key && levels.includes(i.level));
      const total = itemsForLevel.length;
      const completed = itemsForLevel.filter(item => itemStatus[item.id]?.isCompleted).length;
      const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
      return { key, progress, items: total, completed };
    });
  }, [getTimeLevelsForGoal, itemStatus]);

  // Lägg till favorit direkt i inköpslista från Dashboard
  const handleQuickAddFavorite = async (listId: string, favorite: Favorite) => {
    // Kontrollera om artikeln redan finns i listan
    const existingItem = shoppingListItems.find(item => 
      item.listId === listId && 
      item.name.toLowerCase() === favorite.name.toLowerCase()
    );

    if (existingItem) {
      // Om artikeln redan finns, öka bara antalet
      await updateItem(existingItem.id, { quantity: existingItem.quantity + 1 });
    } else {
      // Annars lägg till ny artikel
      await createItem({
        listId,
        name: favorite.name,
        category: favorite.category as InventoryCategory || 'mat',
        notes: favorite.notes || '',
        link: favorite.link || '',
        quantity: 1,
        store: favorite.store || '',
        aisle: favorite.aisle || '',
        purchased: false,
      });
    }
    
    // Visa "lagts till" feedback
    setAddedFavoriteId(favorite.id);
    
    // Ta bort feedback efter en kort delay
    setTimeout(() => {
      setAddedFavoriteId(null);
    }, 1500);
  };

  // VMA dev/test detection (same logic as Layout)
  const getTestDataSetting = () => {
    const appEnv = import.meta.env.VITE_APP_ENV || 'development';
    const isNonProd = !import.meta.env.PROD && (import.meta.env.DEV || appEnv === 'staging' || appEnv === 'development');
    if (!isNonProd) return false;
    const saved = localStorage.getItem('vma-use-test-data');
    return saved !== null ? saved === 'true' : true;
  };

  const [useTestData] = useState(getTestDataSetting);

  interface VmaAlertBrief { id: string; [k: string]: unknown }
  const { data: vmaData } = useQuery<{ alerts: VmaAlertBrief[] }>(
    {
      queryKey: ['vma-alerts-dashboard', vmaRegion, useTestData],
      queryFn: async () => {
        const base = useTestData ? '/vma/alerts?test=true' : '/vma/alerts';
        const sep = base.includes('?') ? '&' : '?';
        const countyCode = vmaRegion ? (await import('@/data/regions')).getCountyCodeForRegion(vmaRegion) : undefined;
        const regionPart = countyCode ? `${sep}counties=${encodeURIComponent(countyCode)}` : (vmaRegion ? `${sep}region=${encodeURIComponent(vmaRegion)}` : '');
        const endpoint = `${base}${regionPart}`;
        const { accessToken } = useAuthStore.getState();
        const url = `${import.meta.env.VITE_API_URL || 'http://localhost:7071/api'}${endpoint}`;
        const res = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
        if (!res.ok) return { alerts: [] };
        const json = await res.json();
        return json.data || { alerts: [] };
      },
      enabled: true,
      staleTime: 30 * 1000,
      refetchInterval: 5 * 60 * 1000,
    }
  );

  const hasActiveVma = vmaData?.alerts && vmaData.alerts.length > 0;

  // Hämta antal pending group invites
  const { data: invitesSummary } = useQuery<{ inviteePending: number; ownerPending: number }>({
    queryKey: ['invites-summary-dashboard'],
    queryFn: async () => {
      const res = await apiGet<{ inviteePending: number; ownerPending: number }>('/invites/summary');
      return res.success && res.data ? (res.data as { inviteePending: number; ownerPending: number }) : { inviteePending: 0, ownerPending: 0 };
    },
    enabled: true,
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });

  // Only count invitations received by user, not invitations they sent
  const pendingInvitesCount = invitesSummary?.inviteePending || 0;

  // Hämta detaljerad info om inbjudningar för att visa gruppnamn
  const { data: pendingInvites } = useQuery<Array<{ id: string; groupName: string }>>({
    queryKey: ['pending-invites-dashboard'],
    queryFn: async () => {
      const res = await apiGet<Array<{ id: string; groupId: string; groupName: string; status: string }>>('/invites');
      if (!res.success || !res.data) return [];
      return res.data.filter(inv => inv.status === 'pending').map(inv => ({ id: inv.id, groupName: inv.groupName }));
    },
    enabled: pendingInvitesCount > 0,
    staleTime: 60 * 1000,
  });

  

  // Hjälpfunktion för att beräkna dagar kvar
  const getDaysUntilText = (dateStr: string): string => {
    const due = new Date(dateStr);
    const now = new Date();
    const days = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (days < 0) return `${Math.abs(days)} dagar sen`;
    if (days === 0) return 'Idag';
    if (days === 1) return 'Imorgon';
    return `${days} dagar`;
  };

  // Show loading screen while initial data is being fetched
  if (isInitialLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4">
      {/* Header med välkomst */}
      <div className="space-y-4">
        <div>
          <div className={`flex items-center justify-between gap-2 mb-1 transition-all duration-300 ${
            desktopSidebarCollapsed ? 'md:pl-10' : ''
          }`}>
              <p className="text-sm text-muted-foreground soft-wrap min-w-0 flex-shrink">
              {t('dashboard.greeting', { name: user?.displayName || 'Medlem' })}
            </p>
            <div className="ml-2 inline-flex items-center gap-2">
              {user?.subscriptionTier === 'premium' ? (
                <Link to="/app/settings#get-premium" className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-200 shadow-sm dark:bg-amber-900/60 dark:text-amber-200 dark:ring-amber-800">
                  <Crown className="h-3 w-3 text-amber-500" />
                  <span className="leading-none">{t('prepLevels.premium')}</span>
                </Link>
              ) : (
                <Link to="/app/settings#get-premium" className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary ring-1 ring-primary/20 shadow-sm dark:bg-primary/20 dark:text-primary-200 dark:ring-primary/30">
                  <span className="h-3 w-3 rounded-full border-2 border-white/90 bg-transparent dark:border-white/10" />
                  <span className="leading-none">{t('dashboard.freeLabel') || 'Gratis'}</span>
                </Link>
              )}

              {user?.familyId && (
                <Link to="/app/family" className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-800 ring-2 ring-white/90 ring-offset-1 ring-offset-card shadow-sm dark:bg-slate-900/60 dark:text-slate-200 dark:ring-white/10" title={t('family.title')}
                >
                  <Users className="h-3 w-3 text-slate-500" />
                  <span className="leading-none">{t('family.title') || t('family')}</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Bannermeddelanden - Svävande under headern (ligger under överliggande menyer) */}
        <div className="sticky top-0">
          {(supportSummary?.unreadCount || 0) > 0 && (
            <button
              type="button"
              onClick={() => navigate('/app/support')}
              className="w-full border-b border-white/10 bg-[#fff8f3] px-4 py-3 text-left text-sm text-[#5f3819] transition-colors hover:bg-[#fff1e6]"
            >
              <span className="font-semibold">Du har {supportSummary?.unreadCount} nya supportmeddelanden.</span>
              <span className="ml-2 underline underline-offset-2">Öppna Support</span>
            </button>
          )}
          <BannerDisplay location="dashboard" />
        </div>

        {/* Banner Carousel - Full Width */}
        <div className="-mx-6 md:-mx-8">
          <div 
            className="relative w-full border-y border-white/10 overflow-hidden bg-black/40 backdrop-blur-md cursor-pointer select-none" 
            style={{ height: '200px' }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onClick={handleCarouselClick}
          >
            <div className="relative w-full h-full">
              {/* Logo banner */}
              <div
                className="absolute inset-0 flex items-center justify-center bg-black transition-transform duration-300 ease-in-out"
                style={{
                  transform: `translateX(${(0 - currentBannerIndex) * 100}%)`,
                }}
              >
                <img 
                  src="/logos/logo-color-textunder.svg" 
                  alt="Logo" 
                  className="h-44 w-auto object-contain"
                />
              </div>
              
              {/* Content banners */}
              {contentBanners.map((banner, index) => {
                const bannerIndex = index + 1; // +1 because logo is at index 0
                return (
                  <div
                    key={banner.id}
                    className="absolute inset-0 transition-transform duration-300 ease-in-out"
                    style={{
                      transform: `translateX(${(bannerIndex - currentBannerIndex) * 100}%)`,
                      backgroundImage: `url(${banner.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: isDesktop 
                        ? `40% ${50 + (banner.backgroundPositionY || 0)}%`
                        : 'center 50%',
                    }}
                  >
                    {/* Gradient overlay from dark (left) to slightly lighter (right) behind the text box */}
                    <div className="absolute inset-y-0 left-0 w-4/5 bg-gradient-to-r from-black/95 via-black/80 to-transparent md:inset-0 md:w-full md:bg-gradient-to-r md:from-black/95 md:via-black/85 md:to-black/5" />
                    <div className="relative z-10 flex flex-col items-start justify-center h-full pointer-events-none pl-8 md:pl-6 py-5 md:py-7">
                      <div className="flex flex-col justify-start px-5 pt-4 pb-5 md:px-8 md:py-7 max-w-[75%] md:max-w-[55%] pointer-events-auto rounded-sm">
                        <h2 className="text-xl md:text-2xl font-bold mb-1 leading-snug" style={{ color: '#f4f3ef' }}>{banner.title}</h2>
                        <p className="text-[13px] md:text-sm leading-snug m-0" style={{ color: '#e8e6e0' }}>{banner.description}</p>
                        <p className="text-[13px] md:text-sm font-medium mt-1 md:mt-3 m-0" style={{ color: '#ed8b00' }}>Läs mer</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Swipe indicator - right arrow */}
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <div className="flex items-center gap-1 opacity-40">
                <ChevronRight className="h-6 w-6 text-white animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <h1 className="text-2xl font-bold mt-6 flex items-center">
            {(() => {
              // Compute the heading text separately so JSX remains clear.
              const familyOwnerHousehold = useFamilyStore.getState().family?.ownerHouseholdDisplayName as string | undefined;
              const householdName = familyOwnerHousehold || user?.settings?.householdDisplayName;
              const titleText = householdName ? `${householdName}${householdName.toLowerCase().endsWith('s') ? '' : 's'} beredskap` : t('dashboard.title');
              return (<>
                <Home className="h-5 w-5 text-primary mr-3 shrink-0" />
                {titleText}
              </>);
            })()}
          </h1>
        </div>
      </div>

      <NativePremiumNoticeModal
        open={showNativePremiumNotice}
        onClose={() => setShowNativePremiumNotice(false)}
        platform={nativePlatform}
      />

      <div className="flex w-full flex-col gap-3">
          {/* VMA alert card shown when active */}
          {hasActiveVma && (
            <Link to="/app/vma" className="block rounded-xl border border-red-600 bg-red-700/10 p-4 pr-10 sm:pr-12 relative hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-red-400">{t('vma.hasAlerts') || t('vma.title') || 'VMA: Viktig information'}</h2>
                  <p className="text-xs text-red-200">
                    {t('vma.info.description') || 'Det finns aktiva VMA-varningar för din region.'}
                    {useTestData && <span className="ml-2 inline-block text-xs font-mono text-red-300">(Testmode)</span>}
                  </p>
                </div>
              </div>
            </Link>
          )}
          {/* Beredskapstestet - visas endast om man inte kört det. Hide for family members. */}
          {(!isInFamily || isFamilyOwner) && !hasTest && !hidePrepTestCard && (
            <div className="rounded-xl bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/30 p-4 pr-10 sm:pr-12 relative">
              <button
                aria-label={t('prepTest.card.dismiss')}
                onClick={() => setHidePrepTestCard(true)}
                className="absolute right-3 top-3 p-1 rounded-full hover:bg-muted"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <ClipboardCheck className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold">{t('prepTest.card.title')}</h2>
                    <p className="text-xs text-muted-foreground">{t('prepTest.card.description')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPrepTest(true)}
                  className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium whitespace-nowrap shrink-0 text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#67724e' }}
                >
                  {t('prepTest.card.startButton')}
                </button>
              </div>
            </div>
          )}

          {/* Gruppinbjudningar - visas när man har pending invites */}
          {pendingInvitesCount > 0 && (
            <button
              onClick={() => {
                setAppMode('group');
                navigate('/app/groups/notifications');
              }}
              className="w-full text-left rounded-xl bg-gradient-to-r from-orange-900/20 to-orange-900/5 border border-orange-700/30 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(148, 81, 35, 0.2)' }}>
                  <MessageSquare className="h-5 w-5" style={{ color: '#945123' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h2 className="text-sm font-semibold">Gruppinbjudningar</h2>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="px-3 py-1 rounded-full text-white text-sm font-medium" style={{ backgroundColor: '#945123' }}>
                        {pendingInvitesCount}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  {pendingInvites && pendingInvites.length > 0 && (
                    <ul className="text-xs text-muted-foreground space-y-0.5 list-disc list-inside">
                      {pendingInvites.slice(0, 3).map(inv => (
                        <li key={inv.id} className="truncate">{inv.groupName}</li>
                      ))}
                      {pendingInvites.length > 3 && (
                        <li>... och {pendingInvites.length - 3} till</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </button>
          )}

                  {/* Huvudkort + Mål/Hushåll i 2-kolumnslayout på stora skärmar */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
                  {/* Vänsterkolumn: Hushållets beredskapsnivå - klickbar till Checklista */}
                    <Link
                      to="/app/checklists"
                      className="rounded-xl border border-primary/30 p-6 hover:shadow-md transition-shadow"
                      style={{ backgroundColor: 'rgba(201,199,132,0.15)' }}
                      title={t('dashboard.cards.checklists')}
                    >
                      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                      <div className="flex-1">
                        <h2 className="text-lg font-semibold mb-4">{t('dashboard.prepLevel')}</h2>
                        <div className="flex items-start gap-4">
                          <div className="text-5xl font-bold" style={{ color: '#869b6f' }}>{prepLevelPercent}%</div>
                          <div className="pb-1 text-muted-foreground">
                            <p className="text-sm mb-1">Baserat på hushållets beredskapsmål, förråd och förberedande åtgärder.</p>
                            <p className="text-sm">
                              {t('dashboard.prepLevelActions', { completed: stats.completedItems, total: stats.totalItems })}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 h-3 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${prepLevelPercent}%`, backgroundColor: '#869b6f' }}
                          />
                        </div>
                      </div>
                    </div>
                  </Link>

                  {/* Högerkolumn: Beredskapsmål + Hushåll staplade */}
                  <div className="flex flex-col gap-3 h-full">
            <Link 
              to="/app/settings#prep-goal"
              className="flex flex-1 items-center gap-2 rounded-lg border border-border px-4 py-2 hover:border-primary/50 hover:bg-primary/10 transition-colors w-full justify-between"
              style={{ backgroundColor: 'rgba(201,199,132,0.10)' }}
            >
            <span className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">{t('dashboard.prepGoalLabel')}:</span>
            </span>
            <span className="font-semibold text-primary">{
              // If in family, prefer owner's prep goal if available (ownerPrepGoal provided by family API)
              (isInFamily && useFamilyStore.getState().family?.ownerPrepGoal)
                ? t(`prepLevels.${useFamilyStore.getState().family?.ownerPrepGoal}`)
                : t(`prepLevels.${prepGoal}`)
            }</span>
            </Link>
            <Link
              to="/app/settings#family"
              className="flex flex-1 items-start justify-between gap-2 rounded-lg border border-border px-4 py-4 hover:border-primary/50 hover:bg-primary/10 transition-colors w-full"
              title={t('dashboard.householdCard.cta')}
              style={{ backgroundColor: 'rgba(201,199,132,0.10)' }}
            >
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">{t('dashboard.householdCard.label')}</span>
            </div>
            <div className="text-xs leading-snug flex flex-col items-start text-left min-w-0 gap-0.5">
              <p className="flex items-center justify-between gap-3 w-full">
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-primary" style={{ fontVariantNumeric: 'tabular-nums' }}>{adultsCount}</span>
                  <span className="text-muted-foreground">{t('dashboard.householdCard.adults')}</span>
                </span>
                <span className="text-muted-foreground text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>{adultsKcal} kcal/dag</span>
              </p>
              <p className="flex items-center justify-between gap-3 w-full">
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-primary" style={{ fontVariantNumeric: 'tabular-nums' }}>{childrenCount}</span>
                  <span className="text-muted-foreground">{t('dashboard.householdCard.children')}</span>
                </span>
                <span className="text-muted-foreground text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>{childrenKcal} kcal/dag</span>
              </p>
              <p className="flex items-center justify-between gap-3 w-full">
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-primary" style={{ fontVariantNumeric: 'tabular-nums' }}>{petCount}</span>
                  <span className="text-muted-foreground">{t('dashboard.householdCard.pets')}</span>
                </span>
              </p>
              <div className="w-full border-t border-border/50 mt-1 pt-1">
                <p className="flex items-center justify-between gap-3 w-full">
                  <span className="text-muted-foreground font-medium">Totalt:</span>
                  <span className="font-semibold text-primary text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>{totalDailyKcal} kcal/dag</span>
                </p>
              </div>
            </div>
            </Link>
                  </div>{/* end högerkolumn */}
                  </div>{/* end yttre 2-kolumnsgrid */}
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/app/water"
              onClick={() => { try { sessionStorage.removeItem('scroll:/app/water'); } catch { /* noop */ } }}
              className="rounded-xl bg-blue-500/10 border border-blue-500/20 p-4 hover:bg-blue-500/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Droplets className="h-5 w-5 text-blue-400" aria-hidden />
                <h3 className="text-sm font-semibold text-blue-500">{t('dashboard.summary.water.title')}</h3>
              </div>
              <p className="mt-4 text-2xl sm:text-3xl font-bold text-blue-500">
                {t('dashboard.summary.water.ready', { days: Math.floor(waterStats.daysSupply ?? 0) })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                <span className="block">{t('dashboard.summary.water.stored', { totalLiters: Math.round(waterStats.totalLiters) })}</span>
                <span className="block">{t('dashboard.summary.water.goalLabel', { days: useWaterStore.getState().targetDays, liters: Math.round(waterStats.targetLiters) })}</span>
              </p>
              <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${Math.round(waterStats.progress ?? 0)}%`, backgroundColor: '#3b82f6' }}
                />
              </div>
            </Link>
            <Link
              to="/app/inventory"
              onClick={() => { try { sessionStorage.removeItem('scroll:/app/inventory'); } catch { /* noop */ } }}
              className="rounded-xl bg-orange-400/10 border border-orange-400/30 p-4 hover:bg-orange-400/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Utensils className="h-5 w-5 text-orange-500" aria-hidden />
                <h3 className="text-sm font-semibold text-orange-600">{t('dashboard.summary.calories.title')}</h3>
              </div>
              <p className="mt-4 text-2xl sm:text-3xl font-bold text-orange-600">
                {t('dashboard.summary.calories.ready', { days: daysOfFood })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                <span className="block">{t('dashboard.summary.calories.stored', { total: totalKcal.toLocaleString() })}</span>
                <span className="block">{t('dashboard.summary.calories.goalLabel', { days: PREP_GOAL_DAYS[prepGoal] ?? 7, kcal: caloriesGoal.toLocaleString() })}</span>
              </p>
              <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${Math.round(caloriesPercent ?? 0)}%`, backgroundColor: '#fb923c' }}
                />
              </div>
            </Link>
          </div>

          {/* Meal Plan Link - Show if at least one recipe in queue */}
          {mealPlanItems.length > 0 && (
            <div>
              <button
                onClick={() => {
                  navigate('/app/recipes', { state: { openMealPlan: true } });
                }}
                className="w-full rounded-xl p-4 transition-all flex items-center justify-between group border"
                style={{ 
                  backgroundColor: 'rgba(109, 124, 72, 0.10)', 
                  borderColor: 'rgba(109, 124, 72, 0.30)' 
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(109, 124, 72, 0.20)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(109, 124, 72, 0.10)';
                }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <UtensilsCrossed className="h-6 w-6" style={{ color: '#83a359' }} aria-hidden />
                  <p className="text-sm soft-wrap" style={{ color: '#83a359' }}>
                    <span className="font-semibold">Matsedel:</span> {
                      (() => {
                        // Find first recipe, ignoring markers
                        const nextRecipe = mealPlanItems.find(item => item.recipeId && !item.markerLabel);
                        return nextRecipe?.recipeName || 'Tom matsedel';
                      })()
                    }
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform flex-shrink-0" style={{ color: '#83a359' }} aria-hidden />
              </button>
            </div>
          )}

          {/* Shopping List + Notes: 2-column layout on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
          {/* Vänster: Inköpslista */}
          <div className="flex flex-col gap-3">
          {/* Shopping List Link */}
          <div ref={shoppingListRef}>
            <Link
              to="/app/shopping-list"
              onClick={() => { try { sessionStorage.removeItem('scroll:/app/shopping-list'); } catch { /* noop */ } }}
              className="w-full rounded-xl p-4 transition-colors flex items-center justify-between group border"
              style={{ backgroundColor: 'rgba(167,139,250,0.15)', borderColor: 'rgba(167,139,250,0.15)' }}
            >
            <div className="flex items-center gap-3">
              <ShoppingCart className="h-5 w-5 text-purple-300" aria-hidden />
              <h3 className="text-sm font-semibold text-purple-300">{t('nav.shoppingList')}</h3>
            </div>
            <ChevronRight className="h-5 w-5 text-purple-300 group-hover:translate-x-1 transition-transform" aria-hidden />
            </Link>
          </div>

          {/* Primary Shopping List Shortcut */}
          {(shoppingLists || []).filter(list => list.isPrimary).map((list) => (
            <div key={list.id} className="-mt-3">
              <div
                className="rounded-lg py-2 pl-4 pr-4 ml-4 mr-0 transition-colors flex items-center gap-4 group"
                style={{ backgroundColor: 'rgba(167,139,250,0.08)', borderLeft: '2px solid rgba(167,139,250,0.3)', width: 'calc(100% - 1rem)' }}
              >
                <Link
                  to={`/app/shopping-list?listId=${list.id}`}
                  className="flex items-center gap-2 flex-1 min-w-0"
                >
                  <Circle className="h-2 w-2 flex-shrink-0 fill-current text-purple-300" aria-hidden />
                  <span className="text-sm truncate text-purple-300">{list.name}</span>
                </Link>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const isOpening = showQuickAddFavorite !== list.id;
                        setShowQuickAddFavorite(showQuickAddFavorite === list.id ? null : list.id);
                        setQuickAddFavoriteSearch('');
                        
                        // Scrolla till inköpslistkortet när dropdown öppnas
                        if (isOpening && shoppingListRef.current) {
                          requestAnimationFrame(() => {
                            if (shoppingListRef.current) {
                              const elementPosition = shoppingListRef.current.getBoundingClientRect().top;
                              const offsetPosition = elementPosition + window.pageYOffset - 80;
                              window.scrollTo({
                                top: offsetPosition,
                                behavior: 'smooth'
                              });
                            }
                          });
                        }
                      }}
                      className="rounded-md p-1.5 transition-all hover:bg-purple-500/20 ring-1 ring-purple-400/40 hover:ring-purple-400/60"
                      title="Lägg till favorit snabbt"
                    >
                      <div className="relative h-3.5 w-3.5">
                        <Heart className="h-3.5 w-3.5 absolute text-purple-300" aria-hidden />
                        <Plus className="h-2 w-2 absolute bottom-0 right-0 bg-background rounded-full text-purple-300" strokeWidth={3} aria-hidden />
                      </div>
                    </button>
                    {showQuickAddFavorite === list.id && (
                      <div 
                        className="quick-add-fav-dropdown absolute right-0 top-full mt-1 w-64 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-2 border-b border-border">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                              type="text"
                              value={quickAddFavoriteSearch}
                              onChange={(e) => setQuickAddFavoriteSearch(e.target.value)}
                              placeholder="Sök favoriter..."
                              className="w-full pl-8 pr-3 py-1.5 bg-background border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto flex-1">
                          {filteredFavorites.length === 0 ? (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              {quickAddFavoriteSearch ? 'Inga favoriter matchar sökningen' : 'Inga favoriter sparade'}
                            </div>
                          ) : (
                            filteredFavorites.map((fav) => {
                              const alreadyInList = shoppingListItems.some(item => 
                                item.listId === list.id && 
                                item.name.toLowerCase() === fav.name.toLowerCase()
                              );
                              
                              return (
                                <button
                                  key={fav.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickAddFavorite(list.id, fav);
                                  }}
                                  disabled={addedFavoriteId === fav.id}
                                  className={`w-full text-left px-3 py-2 transition-colors border-b border-border last:border-b-0 relative ${
                                    addedFavoriteId === fav.id 
                                      ? 'bg-green-500/20 cursor-default' 
                                      : 'hover:bg-muted'
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm">{fav.name}</div>
                                      {(fav.store || fav.aisle) && (
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                          {[fav.store, fav.aisle].filter(Boolean).join(' • ')}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-shrink-0">
                                      {addedFavoriteId === fav.id ? (
                                        <Check className="h-5 w-5 text-green-500 animate-in fade-in zoom-in duration-300" />
                                      ) : alreadyInList ? (
                                        <Check className="h-5 w-5 text-orange-500/80" />
                                      ) : null}
                                    </div>
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setQuickAddListId(list.id);
                      setQuickAddItemForm({
                        name: '',
                        category: 'mat',
                        notes: '',
                        link: '',
                        quantity: 1,
                        price: '0',
                        store: list.store || '',
                        aisle: '',
                      });
                      setShowQuickAddItem(true);
                    }}
                    className="rounded-md p-1.5 transition-all hover:bg-purple-500/20 ring-1 ring-purple-400/40 hover:ring-purple-400/60"
                    title="Lägg till artikel"
                  >
                    <Plus className="h-3.5 w-3.5 text-purple-300" aria-hidden />
                  </button>
                </div>
              </div>
            </div>
          ))}

          </div>{/* end vänster inköpslista-kolumn */}

          {/* Höger: Anteckningar */}
          <div className="flex flex-col gap-3">
          {/* Notes Link */}
          <Link
            to="/app/notes"
            onClick={() => { try { sessionStorage.removeItem('scroll:/app/notes'); } catch { /* noop */ } }}
            className="w-full rounded-xl p-4 transition-colors flex items-center justify-between group border"
            style={{ backgroundColor: 'rgba(244,194,194,0.15)', borderColor: 'rgba(244,194,194,0.15)' }}
          >
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5" style={{ color: '#e8b4b4' }} aria-hidden />
              <h3 className="text-sm font-semibold" style={{ color: '#e8b4b4' }}>{t('nav.notes')}</h3>
            </div>
            <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" style={{ color: '#e8b4b4' }} aria-hidden />
          </Link>

          {/* Pinned Notes */}
          {notes.filter(n => n.isPinned).length > 0 && (
            <div className="-mt-3 flex flex-col gap-0">
              {notes.filter(n => n.isPinned).sort((a, b) => a.title.localeCompare(b.title)).map((note) => (
                <button
                  key={note.id}
                  onClick={() => setViewingNote(note)}
                  className="rounded-lg py-2 pl-4 pr-4 ml-4 mr-0 transition-colors flex items-center gap-2 group text-left"
                  style={{ backgroundColor: 'rgba(232,180,180,0.08)', borderLeft: '2px solid rgba(232,180,180,0.3)', width: 'calc(100% - 1rem)' }}
                >
                  <Circle className="h-2 w-2 flex-shrink-0 fill-current" style={{ color: '#e8b4b4' }} aria-hidden />
                  <span className="text-sm truncate" style={{ color: '#e8b4b4' }}>{note.title}</span>
                </button>
              ))}
            </div>
          )}

          </div>{/* end höger anteckningar-kolumn */}
          </div>{/* end 2-column grid */}

          {/* Rekommendationer + Underhåll: 2-column på desktop */}
          <div className={`grid grid-cols-1 gap-3 items-start ${ (hasTest && recommendations.filter(r => !r.completed).length > 0 && (isInFamily && !isFamilyOwner ? prepTestLoaded : true)) || dashboardLists.length > 0 ? 'lg:grid-cols-2' : '' }`}>
            <div className="flex flex-col gap-3">
          {/* Personliga rekommendationer baserat på beredskapstestet */}
          {/* For family members, only show after data is loaded from server to ensure we show owner's recommendations */}
          {hasTest && recommendations.length > 0 && recommendations.filter(r => !r.completed).length > 0 && (isInFamily && !isFamilyOwner ? prepTestLoaded : true) && (
            <div id="recommendations-card" className="rounded-xl border border-amber-500/30 p-4" style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold">{t('dashboard.recommendations.title')}</h2>
              </div>
              <div className="text-sm text-muted-foreground mb-4 space-y-2">
                {isInFamily && !isFamilyOwner && (
                  <p className="text-amber-600 font-medium">
                    Endast familjeägaren kan göra om beredskapstestet.
                  </p>
                )}
                <p>
                  {isInFamily 
                    ? 'Det här är familjens personliga rekommendationer som hjälper er att komma igång med hushållets beredskapsarbete.'
                    : 'Det här är dina personliga rekommendationer som hjälper dig att komma igång med ditt beredskapsarbete.'
                  }
                </p>
                <p>
                  {isInFamily
                    ? <>Ni kan också höja hushållets beredskapsnivå genom att bygga upp era beredskapsförråd på sidorna <Link to="/app/water" className="text-primary hover:underline">Vatten</Link> och <Link to="/app/inventory" className="text-primary hover:underline">Förråd</Link>.</>
                    : <>Du kan också höja hushållets beredskapsnivå genom att bygga upp dina beredskapsförråd på sidorna <Link to="/app/water" className="text-primary hover:underline">Vatten</Link> och <Link to="/app/inventory" className="text-primary hover:underline">Förråd</Link>.</>
                  }
                </p>
              </div>
              <div className="space-y-3">
                  {recommendations.filter(r => !r.completed).slice(0, 3).map((rec) => {
                  const areaCfg = recommendationAreaConfig[rec.area] || recommendationFallbackArea;
                  const Icon = areaCfg.icon;
                  const isCompleting = completingRecommendationIds.has(rec.id);
                  return (
                    <div
                      key={rec.id}
                      className={`flex items-start gap-4 rounded-lg border p-4 transition-all ${
                        isCompleting
                          ? 'border-green-500/30 bg-green-500/5 opacity-60'
                          : 'border-border bg-card'
                      }`}
                    >
                      {/* Vertikal layout: Områdes-ikon överst, checkbox under */}
                      <div className="flex flex-col items-center gap-2 flex-shrink-0">
                        {/* Områdes-ikon */}
                        <div
                          className="rounded-lg p-2"
                          style={{ backgroundColor: `${areaCfg.color}20` }}
                        >
                          <Icon className="h-5 w-5" style={{ color: areaCfg.color }} />
                        </div>
                        
                        {/* Checkbox */}
                        <div 
                          className="cursor-pointer"
                          onClick={() => handleToggleRecommendation(rec.id)}
                        >
                          {isCompleting ? (
                            <div className="rounded p-1.5 bg-green-500/20 border-2 border-green-500/40">
                              <Check className="h-4 w-4 text-green-500" />
                            </div>
                          ) : (
                            <div className="rounded p-1.5 border-2 border-border hover:border-green-500/40 transition-colors">
                              <div className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="mb-1">
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              rec.priority === 'high'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : rec.priority === 'medium'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }`}
                          >
                            {t(`dashboard.recommendations.priority.${rec.priority}`)}
                          </span>
                        </div>
                        <h3 className={`text-sm font-medium mb-1 ${isCompleting ? 'line-through' : ''}`}>{rec.title}</h3>
                        <p className={`text-sm mb-1 ${isCompleting ? 'line-through text-muted-foreground' : 'text-muted-foreground'}`}>{rec.description}</p>
                        {rec.actionUrl && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer bg-transparent border-0 p-0"
                            onClick={() => {
                              const currentScrollY = window.scrollY || 0;
                              console.log(`[Dashboard] onClick - scrollY: ${currentScrollY}`);
                              
                              try {
                                // Save scroll position
                                sessionStorage.setItem('scroll:/app', String(currentScrollY));
                                window.__preservedScrollY = currentScrollY;
                                
                                console.log(`[Dashboard] Saved scroll:/app = ${currentScrollY}`);
                                
                                // Navigate
                                if (rec.actionUrl) {
                                  navigate(rec.actionUrl, { 
                                    state: { from: '/app', preserveScrollY: currentScrollY }
                                  });
                                  
                                  // IMMEDIATELY restore scroll after navigate
                                  setTimeout(() => {
                                    console.log(`[Dashboard] Restoring scroll to ${currentScrollY} after navigate`);
                                    window.scrollTo(0, currentScrollY);
                                  }, 0);
                                }
                              } catch {
                                // Ignore errors
                              }
                            }}
                          >
                            <ExternalLink className="h-3 w-3" />
                            Läs mer
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Visa alla knapp */}
              <div className="mt-4 pt-4 border-t border-amber-500/20">
                <button
                  onClick={() => setShowRecommendationsModal(true)}
                  className="w-full py-2 px-4 rounded-lg border border-amber-500/30 bg-card hover:bg-amber-500/10 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  Visa alla ({recommendations.filter(r => !r.completed).length})
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Egna listor som ska visas på Översikt */}
          {dashboardLists.map(list => {
        const listItems = customItems
          .filter(item => item.listId === list.id)
          .sort((a, b) => {
            const orderA = typeof a.order === 'number' ? a.order : 0;
            const orderB = typeof b.order === 'number' ? b.order : 0;
            return orderA - orderB;
          });
        const completedCount = listItems.filter(item => item.completed).length;
        const totalCount = listItems.length;
        const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
        const listColor = list.color || '#ff6b35';

        return (
          <div key={list.id} className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <Link 
                to="/app/checklists"
                state={{ scrollToListId: list.id }}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <ClipboardList className="h-5 w-5" style={{ color: listColor }} />
                <h2 className="text-xl font-semibold">{list.name}</h2>
              </Link>
              <Link 
                to="/app/checklists" 
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                Checklista <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="flex-1 h-3 rounded-full bg-muted/30 border border-border overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                  aria-label={`Progress för ${list.name}`}
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${progress}%`, backgroundColor: listColor }}
                  />
                </div>
                <span className="text-sm text-muted-foreground whitespace-nowrap">{completedCount}/{totalCount}</span>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 divide-y divide-border">
              {listItems.length === 0 ? (
                <div className="p-8 text-center">
                  <ClipboardList className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">Inga aktiviteter i listan</p>
                </div>
              ) : (
                <>
                  {listItems.slice(0, 10).map(item => (
                    <div
                      key={item.id}
                      onClick={() => toggleCustomItem(item.id)}
                      className="flex items-center gap-3 py-4 pl-2 pr-4 hover:bg-muted/50 transition-colors cursor-pointer"
                    >
                      {/* Checkbox */}
                      <div className="flex-shrink-0">
                        {item.completed ? (
                          <div className="rounded p-1.5 border-2" style={{ backgroundColor: `${listColor}20`, borderColor: `${listColor}40` }}>
                            <Check className="h-4 w-4" style={{ color: listColor }} />
                          </div>
                        ) : (
                          <div className="rounded p-1.5 border-2 border-border">
                            <div className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                      
                      {/* Text */}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className={`font-medium break-words ${item.completed ? 'line-through opacity-60' : ''}`} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                          {item.text}
                        </p>
                      </div>
                    </div>
                  ))}
                  {listItems.length > 10 && (
                    <Link
                      to="/app/checklists"
                      state={{ scrollToListId: list.id }}
                      className="block py-3 text-center text-sm text-primary hover:bg-muted/50 transition-colors"
                    >
                      Visa alla ({listItems.length})
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

            </div>{/* end vänster kolumn: rekommendationer + egna listor */}

            {/* Höger kolumn: Underhåll */}
            <div className="rounded-lg border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <Link 
            to="/app/yearwheel"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <CalendarDays className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">{`Underhåll ${monthNameCapitalized}`}</h2>
          </Link>
          <Link 
            to="/app/yearwheel" 
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            {t('nav.yearwheel')} <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        
        {/* Varning för försenade uppgifter */}
        {overdueTasks.length > 0 && (
          <Link 
            to="/app/yearwheel"
            state={{ viewMode: 'overdue' }}
            className="flex items-center gap-3 p-4 mb-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors"
          >
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-red-700 dark:text-red-400">
                {overdueTasks.length} {t('yearWheel.overdueTasks')}
              </p>
              <p className="text-sm text-red-600 dark:text-red-500">
                {overdueTasks.slice(0, 2).map(t => t.title).join(', ')}
                {overdueTasks.length > 2 && '...'}
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-red-400" />
          </Link>
        )}
        
        <div className="rounded-lg border border-border bg-muted/30 divide-y divide-border">
          {currentMonthTasks.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Check className="h-6 w-6 text-primary" />
              </div>
              <p className="text-muted-foreground">{t('yearWheel.noTasksThisMonth', 'Inga uppgifter planerade denna månad')}</p>
            </div>
          ) : (
            <>
              {currentMonthTasks.slice(0, 10).map((task) => {
                const areaKey = task.area as PrepAreaKey;
                const config = yearWheelAreaConfig[areaKey] || yearWheelAreaConfig.safety;
                const Icon = config.icon;
                const completed = task.lastCompleted ? (new Date(task.lastCompleted).getFullYear() === currentYear && new Date(task.lastCompleted).getMonth() === currentMonth) : false;
                return (
                  <Link 
                    key={task.id} 
                    to="/app/yearwheel"
                    state={{ scrollToTaskId: task.id }}
                    className="flex items-center gap-3 py-4 pl-2 pr-4 hover:bg-muted/50 transition-colors"
                  >
                    {/* Vertikal layout: Områdes-ikon överst, checkbox under */}
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      {/* Områdes-ikon */}
                      <div 
                        className={`rounded-lg p-2 ${config.color}`}
                      >
                        <Icon className={`h-4 w-4 ${config.iconColor}`} />
                      </div>
                      
                      {/* Checkbox - orange och mindre, med tydlig ram */}
                      <div>
                        {completed ? (
                          <div className="rounded p-1.5 bg-orange-500/20 border-2 border-orange-500/40">
                            <Check className="h-4 w-4 text-orange-500" />
                          </div>
                        ) : (
                          <div className="rounded p-1.5 border-2 border-border">
                            <div className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Titel och område */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="font-medium break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {task.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {config.name}
                      </p>
                    </div>
                    
                    {/* Tidsinformation */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground flex-shrink-0">
                      {!completed ? (
                        <>
                          <Clock className="h-4 w-4" />
                          {getDaysUntilText(task.nextDue)}
                        </>
                      ) : (
                        <span className="text-sm text-muted-foreground">{t('dashboard.completed', 'Utförd')}</span>
                      )}
                    </div>
                  </Link>
                );
              })}
              {currentMonthTasks.length > 10 && (
                <Link
                  to="/app/yearwheel"
                  className="block py-3 text-center text-sm text-primary hover:bg-muted/50 transition-colors"
                >
                  Visa alla ({currentMonthTasks.length})
                </Link>
              )}
            </>
          )}
        </div>
            </div>{/* end Underhåll + höger kolumn */}
          </div>{/* end 2-column grid rekommendationer+underhåll */}
      </div>{/* end flex w-full flex-col gap-3 */}


      {/* PrepTest Modal */}
      {/* Beredskapsområden */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <Link 
            to="/app/checklists"
            className="hover:opacity-80 transition-opacity"
          >
            <h2 className="text-xl font-semibold">Beredskapsåtgärder ({PREP_GOAL_DAYS[prepGoal] ?? 7} dagar)</h2>
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {prepAreas.map((area) => {
            const cfg = areaConfig[area.key];
            const Icon = cfg.icon;
            return (
              <Link
                key={area.key}
                to={`/app/checklists?area=${area.key}`}
                className="group rounded-lg border border-border bg-card p-4 transition-all hover:border-primary/50 hover:shadow-md"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="rounded-lg p-2.5"
                    style={{ backgroundColor: `${cfg.color}20` }}
                  >
                    <Icon 
                      className="h-5 w-5" 
                      style={{ color: cfg.color }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate group-hover:text-primary transition-colors">
                      {t(`prepAreas.${area.key}`)}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {t('dashboard.areaItemsComplete', { completed: area.completed, total: area.items })}
                    </p>
                  </div>
                  <div className="text-right">
                      <span className="text-lg font-semibold" style={{ color: cfg.color }}>
                      {area.progress}%
                    </span>
                  </div>
                </div>
                <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ 
                      width: `${area.progress}%`,
                      backgroundColor: cfg.color 
                    }}
                  />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      {showPrepTest && (
        <PrepTestModal 
          onClose={() => setShowPrepTest(false)} 
          isFirstTime={!hasTest}
        />
      )}
      <RecommendationsModal 
        isOpen={showRecommendationsModal}
        onClose={() => {
          setShowRecommendationsModal(false);
          setHasSeenRecommendations(true);
        }}
        onStartTest={() => {
          setShowRecommendationsModal(false);
          setShowPrepTest(true);
        }}
      />

      {/* Note Viewing Modal */}
      {viewingNote && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center" 
          onClick={() => setViewingNote(null)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}
        >
          <div 
            className="bg-card rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] m-4 overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-2xl font-bold text-foreground">{viewingNote.title}</h2>
              <button
                onClick={() => setViewingNote(null)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="prose prose-slate dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                  {viewingNote.content}
                </ReactMarkdown>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border bg-muted/30">
              <button
                onClick={() => setViewingNote(null)}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Stäng
              </button>
              <Link
                to="/app/notes"
                state={{ openNoteId: viewingNote.id }}
                className="px-4 py-2 rounded-lg text-white font-medium transition-colors"
                style={{ backgroundColor: '#67724e' }}
              >
                Redigera
              </Link>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Quick Add Item Modal */}
      {showQuickAddItem && quickAddListId && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Lägg till artikel</h3>
              <button 
                onClick={() => setShowQuickAddItem(false)} 
                className="p-1 rounded hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Artikelnamn</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={quickAddItemForm.name}
                    onChange={(e) => setQuickAddItemForm(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder="Artikelnamn"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSuggestionSearch('');
                      setShowSuggestions(true);
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa förslag"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Kategori</label>
                <select
                  value={quickAddItemForm.category}
                  onChange={(e) => setQuickAddItemForm(prev => ({ ...prev, category: e.target.value as InventoryCategory | '' }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  <option value="">Ingen kategori</option>
                  <option value="mat">{CATEGORY_LABELS.mat}</option>
                  <option value="medicin">{CATEGORY_LABELS.medicin}</option>
                  <option value="hygien">{CATEGORY_LABELS.hygien}</option>
                  <option value="verktyg">{CATEGORY_LABELS.verktyg}</option>
                  <option value="kommunikation">{CATEGORY_LABELS.kommunikation}</option>
                  <option value="varme">{CATEGORY_LABELS.varme}</option>
                  <option value="ljus">{CATEGORY_LABELS.ljus}</option>
                  <option value="dokument">{CATEGORY_LABELS.dokument}</option>
                  <option value="ovrigt">{CATEGORY_LABELS.ovrigt}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Anteckningar</label>
                <textarea
                  value={quickAddItemForm.notes}
                  onChange={(e) => setQuickAddItemForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="Anteckningar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Länk</label>
                <input
                  type="url"
                  value={quickAddItemForm.link}
                  onChange={(e) => setQuickAddItemForm(prev => ({ ...prev, link: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Antal</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setQuickAddItemForm(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                    disabled={quickAddItemForm.quantity <= 1}
                    className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={quickAddItemForm.quantity}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setQuickAddItemForm(prev => ({ ...prev, quantity: Math.max(1, parseInt(val) || 1) }));
                    }}
                    className="w-20 px-3 py-2 text-center rounded-lg border border-border bg-background"
                  />
                  <button
                    type="button"
                    onClick={() => setQuickAddItemForm(prev => ({ ...prev, quantity: prev.quantity + 1 }))}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pris (valfritt)</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setQuickAddItemForm(prev => { const cur = parseFloat((prev.price || '0').replace(',', '.')) || 0; const n = Math.max(0, cur - 100); return { ...prev, price: n % 1 === 0 ? String(n) : String(n).replace('.', ',') }; })}
                    disabled={parseFloat((quickAddItemForm.price || '0').replace(',', '.')) <= 0}
                    className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={quickAddItemForm.price}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9,.]/g, '').replace('.', ',').replace(/,{2,}/g, ',');
                        const parts = raw.split(',');
                        const cleaned = parts[0].slice(0, 7) + (parts.length > 1 ? ',' + parts[1].slice(0, 2) : '');
                        if (cleaned === '' || cleaned === ',') { setQuickAddItemForm(prev => ({ ...prev, price: '0' })); return; }
                        const numVal = parseFloat(cleaned.replace(',', '.'));
                        if (!isNaN(numVal) && numVal <= 1000000) setQuickAddItemForm(prev => ({ ...prev, price: cleaned }));
                      }}
                      className="w-24 px-3 py-2 text-center rounded-lg border border-border bg-background"
                    />
                    <span className="text-sm text-muted-foreground">kr</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setQuickAddItemForm(prev => { const cur = parseFloat((prev.price || '0').replace(',', '.')) || 0; const n = Math.min(1000000, cur + 100); return { ...prev, price: n % 1 === 0 ? String(n) : String(n).replace('.', ',') }; })}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Butik</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={quickAddItemForm.store}
                    onChange={(e) => setQuickAddItemForm(prev => ({ ...prev, store: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder="T.ex. ICA Maxi"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setStoreSuggestionSearch('');
                      setShowStoreSuggestions(true);
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa befintliga butiker"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Hylla/Avdelning</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={quickAddItemForm.aisle}
                    onChange={(e) => setQuickAddItemForm(prev => ({ ...prev, aisle: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder="T.ex. Mejeri"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setAisleSuggestionSearch('');
                      setShowAisleSuggestions(true);
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa avdelningar"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowQuickAddItem(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!quickAddItemForm.name.trim()) return;
                    
                    await createItem({
                      listId: quickAddListId,
                      name: quickAddItemForm.name,
                      category: quickAddItemForm.category || undefined,
                      notes: quickAddItemForm.notes || undefined,
                      link: quickAddItemForm.link || undefined,
                      quantity: quickAddItemForm.quantity,
                      price: parseFloat((quickAddItemForm.price || '0').replace(',', '.')) || 0,
                      store: quickAddItemForm.store || undefined,
                      aisle: quickAddItemForm.aisle || undefined,
                      purchased: false,
                    });
                    
                    setShowQuickAddItem(false);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg text-white hover:opacity-90"
                  style={{ backgroundColor: '#67724e' }}
                >
                  Spara
                </button>
              </div>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Suggestions Modal */}
      {/* Suggestions Modal */}
      {showSuggestions && (
        <ModalBackdrop>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj vara från förslag</h3>
              <button onClick={() => setShowSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={suggestionSearch}
                  onChange={(e) => setSuggestionSearch(e.target.value)}
                  placeholder="Sök vara..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredSuggestions.map((suggestion) => (
                <button
                  key={suggestion.name}
                  onClick={() => {
                    setQuickAddItemForm(prev => ({ ...prev, name: suggestion.name, aisle: suggestion.aisle }));
                    setShowSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="font-medium">{suggestion.name}</div>
                  {suggestion.aisle && (
                    <div className="text-xs text-muted-foreground">{suggestion.aisle}</div>
                  )}
                </button>
              ))}
              {filteredSuggestions.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga förslag hittades
                </p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Store Suggestions Modal */}
      {showStoreSuggestions && (
        <ModalBackdrop>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj butik</h3>
              <button onClick={() => setShowStoreSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={storeSuggestionSearch}
                  onChange={(e) => setStoreSuggestionSearch(e.target.value)}
                  placeholder="Sök butik..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredStores.map((store) => (
                <button
                  key={store}
                  onClick={() => {
                    setQuickAddItemForm(prev => ({ ...prev, store }));
                    setShowStoreSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {store}
                </button>
              ))}
              {filteredStores.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga butiker hittades
                </p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Aisle Suggestions Modal */}
      {showAisleSuggestions && (
        <ModalBackdrop>
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj avdelning</h3>
              <button onClick={() => setShowAisleSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={aisleSuggestionSearch}
                  onChange={(e) => setAisleSuggestionSearch(e.target.value)}
                  placeholder="Sök avdelning..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredAisles.map((aisle) => (
                <button
                  key={aisle}
                  onClick={() => {
                    setQuickAddItemForm(prev => ({ ...prev, aisle }));
                    setShowAisleSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {aisle}
                </button>
              ))}
              {filteredAisles.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga avdelningar hittades
                </p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}

 

